/**
 * Videos attached to a `.trc` recording (`app/services/trc_video_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import { lastValueFrom, tap } from 'rxjs';
import * as client from '@brainfocus/web/api/client';
import { assertPojo } from '@brainfocus/web/api/response';
import type { TrcVideoRecord } from '@brainfocus/web/api/types';

/**
 * Attach the DVIDEO entries and (optional) matched videos parsed from a
 * `.trc` to an already-uploaded recording (issue #564). Offsets are in the
 * uploaded segment's timeline. Videos are optional, so an empty `videos`
 * list is valid and simply persists the expected `dvideoEntries`.
 *
 * Each video is uploaded in its OWN request (instead of one large multipart
 * batch): a single failed/aborted clip no longer drops the whole set, large
 * clips are far more robust, and partial success is possible. Per-clip
 * failures are collected and rethrown so the caller can surface them rather
 * than silently losing videos (#565). Each clip goes through `requestWithProgress`, so it
 * reports its upload progress and fails after the upload timeout instead of hanging (#1211).
 */
export async function uploadTrcVideos(
    payload: UploadTrcVideosPayload,
    hooks: TrcVideoUploadHooks = {},
): Promise<TrcVideosResult> {
    const { edfId, dvideoEntries, videos } = payload;
    const url = `/api/uploaded-edf/${edfId}/videos`;

    // 1. Persist the DVIDEO entries first, independent of the video bytes,
    //    so the expected-clip metadata is stored even if a clip upload fails.
    const entriesForm = new FormData();
    entriesForm.append('dvideoEntries', JSON.stringify(dvideoEntries.map(e => ({
        filename_num: e.filenameNum,
        delay_ms: e.delayMs,
        duration_ms: e.durationMs,
    }))));
    try {
        await client.request('POST', url, entriesForm);
    } catch (err) {
        // Surface this distinctly: when it fails no clip uploads run, so the
        // upload tracker would otherwise show nothing at all.
        const reason = err instanceof Error ? err.message : String(err);
        throw new Error(`Could not store the video timing metadata (no videos were uploaded): ${reason}`);
    }

    // 2. Upload each matched video on its own request, reporting per-clip
    //    start/progress/done/error through `hooks` so the caller can show progress.
    const persisted: TrcVideoRecord[] = [];
    const failed: string[] = [];
    for (const v of videos) {
        const name = v.file.name;
        hooks.onClipStart?.(name);
        try {
            const { result } = await lastValueFrom(client.requestWithProgress<TrcVideosResult>(url, {
                video: v.file,
                filenameNum: String(v.filenameNum),
                offsetSec: String(v.offsetSec),
                durationSec: String(v.durationSec),
                originalFilename: name,
            }).pipe(tap(({ progress }) => {
                if (progress !== undefined) hooks.onClipProgress?.(name, progress);
            })));
            if (result === undefined) throw new Error('The video upload ended without a response');
            assertPojo<TrcVideosResult>(result);
            persisted.push(...result.videos);
            hooks.onClipDone?.(name);
        } catch (err) {
            failed.push(name);
            hooks.onClipError?.(name, err);
        }
    }

    if (failed.length > 0) {
        throw new Error(`Failed to upload ${failed.length} video(s): ${failed.join(', ')}`);
    }
    return { videos: persisted };
}

/**
 * List the persisted `.trc` videos for an uploaded EDF (issue #565). The
 * records are returned sorted by `offsetSec`, without the storage ref.
 */
export async function getTrcVideos(edfId: number): Promise<TrcVideoRecord[]> {
    const response = await client.request('GET', `/api/uploaded-edf/${edfId}/videos`);
    // Validate the shape: an expired session resolves with the login HTML
    // (a string), and `(string).videos` would propagate `undefined` into
    // the playlist and crash the hosting view at render time.
    assertPojo<TrcVideosResult>(response);
    if (!Array.isArray(response.videos)) {
        throw new Error('Unexpected videos response');
    }
    return response.videos;
}

/**
 * Build the URL that streams a single `.trc` video binary. The stream
 * endpoint supports HTTP Range requests, so it can back an
 * `HTMLVideoElement` `src` directly for efficient seeking.
 *
 * Absolute (origin-qualified) so it resolves correctly even when the video
 * element lives in a separate document — e.g. a Document Picture-in-Picture
 * window, whose base URL is otherwise `about:blank` (#565).
 */
export function trcVideoStreamUrl(edfId: number, videoId: number): string {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/api/uploaded-edf/${edfId}/videos/${videoId}/stream`;
}

/** A matched video to attach to an uploaded `.trc`-derived recording. */
export interface TrcVideoUpload {
    file: File;
    filenameNum: number;
    offsetSec: number;
    durationSec: number;
}

/** Per-clip progress callbacks for {@link BrainFocusApi.uploadTrcVideos}. */
export interface TrcVideoUploadHooks {
    onClipStart?: (filename: string) => void;
    /** Fraction of the clip's bytes sent (0..1), as the upload reports it (#1211). */
    onClipProgress?: (filename: string, fraction: number) => void;
    onClipDone?: (filename: string) => void;
    onClipError?: (filename: string, err: unknown) => void;
}

export interface UploadTrcVideosPayload {
    edfId: number;
    /** Expected DVIDEO entries (persisted even when no videos are uploaded). */
    dvideoEntries: Array<{ filenameNum: number; delayMs: number; durationMs: number }>;
    /** Matched videos to store (may be empty when proceeding without video). */
    videos: TrcVideoUpload[];
}

export interface TrcVideosResult {
    videos: TrcVideoRecord[];
}
